import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ParcelForm } from './ArcgisCivil3dParcelSettingForm';
import { useSearchParcelStyles } from '../../../hooks/api/use-parcel-styles.hook';
import { useSearchAutodeskLayers } from '../../../hooks/api/use-autodesk-layers.hook';
import { useGetSettingConfiguration } from '../../../hooks/api/use-arcgis-civil3d-setting-configurations.hook';
import { useSearchArcgisFeatureClasses } from '../../../hooks/api/use-arcgis-feature-classes.hook';
import { AutodeskLayerRelationship, WithLayer } from '../../../../../types/models/autodesk-layer.model';
import { useCreateParcelSetting } from '../../../hooks/api/use-arcgis-civil3d-parcel-settings.hook';

export const AddParcelSetting = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();

  const { loading: loadingParcelStyles, response: parcelStylesResponse } = useSearchParcelStyles({
    limit: 99999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingAutodeskLayers, response: autodeskLayersResponse } = useSearchAutodeskLayers({
    limit: 99999,
    include: [AutodeskLayerRelationship.LAYER],
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingSettingConfiguration, settingConfiguration } = useGetSettingConfiguration({
    settingConfigurationId: Number(configurationSettingId),
  });
  const { loading: loadingFeatureClasses, response: featureClassesResponse } = useSearchArcgisFeatureClasses({
    limit: 99999,
  });

  const [createParcelSetting, { loading: creatingParcel, error: createError }] = useCreateParcelSetting();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ParcelForm>({
    resolver: classValidatorResolver(ParcelForm),
    defaultValues: { configuration: settingConfiguration },
  });

  const handleCreateParcel = async ({
    parcelStyle,
    autodeskLayer,
    configuration,
    featureClass,
    ...values
  }: ParcelForm) => {
    await createParcelSetting({
      ...values,
      parcelStyleId: parcelStyle?.id,
      autodeskLayerId: autodeskLayer?.id,
      configurationId: configuration?.id,
      arcgisFeatureClassId: featureClass.id,
    });
  };

  const onSubmit: SubmitHandler<ParcelForm> = async (values) => {
    await handleCreateParcel(values);

    navigate('..');
  };

  const renderContent = () => (
    <div>
      <Controller
        control={control}
        name="configuration"
        render={({ field }) => (
          <Autocomplete
            {...register('configuration')}
            loading={loadingSettingConfiguration}
            sx={{ mt: '1rem' }}
            disabled
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(configuration) => configuration?.name ?? ''}
            options={[settingConfiguration]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ArcGIS Civil 3D Setting Configuration"
                error={Boolean(errors.configuration)}
                helperText={errors.configuration?.message}
              />
            )}
          />
        )}
      />
      <TextField
        {...register('name')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingParcel}
        error={Boolean(errors.name)}
        helperText={errors.name?.message}
        label="Name"
        margin="dense"
        size="small"
        fullWidth
      />
      <TextField
        {...register('query')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingParcel}
        error={Boolean(errors.query)}
        helperText={errors.query?.message}
        label="Query"
        margin="dense"
        size="small"
        multiline
        rows={4}
        fullWidth
      />
      <TextField
        {...register('priority', { valueAsNumber: true })}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingParcel}
        error={Boolean(errors.priority)}
        helperText={errors.priority?.message}
        label="Priority"
        margin="dense"
        size="small"
        type="number"
        fullWidth
      />
      <TextField
        {...register('site')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingParcel}
        error={Boolean(errors.priority)}
        helperText={errors.priority?.message}
        label="Site"
        margin="dense"
        size="small"
        fullWidth
      />
      <Controller
        control={control}
        name="parcelStyle"
        render={({ field }) => (
          <Autocomplete
            {...register('parcelStyle')}
            loading={loadingParcelStyles}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(parcelStyle) => parcelStyle?.name ?? ''}
            options={parcelStylesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Style"
                error={Boolean(errors.parcelStyle)}
                helperText={errors.parcelStyle?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="autodeskLayer"
        render={({ field }) => (
          <Autocomplete
            loading={loadingAutodeskLayers}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOption) => field.onChange(selectedOption)}
            onBlur={field.onBlur}
            value={field.value ?? null}
            getOptionLabel={(autodeskLayer) => (autodeskLayer as WithLayer)?.layer?.name ?? ''}
            options={autodeskLayersResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Autodesk Layer"
                error={Boolean(errors.autodeskLayer)}
                helperText={errors.autodeskLayer?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="featureClass"
        render={({ field }) => (
          <Autocomplete
            {...register('featureClass')}
            loading={loadingFeatureClasses}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(configuration) => configuration?.name ?? ''}
            options={featureClassesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ArcGIS Feature Class"
                error={Boolean(errors.featureClass)}
                helperText={errors.featureClass?.message}
              />
            )}
          />
        )}
      />
    </div>
  );

  return (
    <Dialog open={true}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
        <DialogTitle>Add ArcGIS Civil 3D Feature Parcel</DialogTitle>
        <DialogContent>
          {renderContent()}
          {createError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {createError?.response?.data?.message || createError?.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Create
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
